import { useCallback, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import { useParams } from 'react-router-dom'
import { NetworkStatus, useQuery } from '@apollo/client'
import { observer } from 'mobx-react'

import { OrdersPreviewColumn } from 'Modules/orders/components/OrdersPreviewColumn/OrdersPreviewColumn'
import { OrdersTable } from 'Modules/orders/components/OrdersTable/OrdersTable'
import { OrdersPreviewColumnTypeEnum } from 'Modules/orders/types/OrdersPreviewColumnType.type'
import { ReturnsPreviewColumn } from 'Modules/returns/components/ReturnsPreviewColumn/ReturnsPreviewColumn'

import { Card } from 'Components/_theme'
import { ContentSidebar } from 'Components/ContentSidebar/ContentSidebar'
import { FaqCard } from 'Components/FaqCard/FaqCard'
import { ImpactTracker } from 'Components/ImpactTracker/ImpactTracker'
import { ListSearch } from 'Components/ListSearch/ListSearch'
import { ObserveMeOnceIAmMounted } from 'Components/ObserveMeOnceIAmMounted/ObserveMeOnceIAmMounted'
import { PageErrorState } from 'Components/PageErrorState/PageErrorState'
import { PageHeader } from 'Components/PageHeader/PageHeader'
import { PageLoadingState } from 'Components/PageLoadingState/PageLoadingState'

import {
  useDefaultListTable,
  useIsOrganisation,
  usePermissions,
  useSidebarVisibility,
} from 'Hooks'

import { hasPermission, toggleIntercomVisibility } from 'Utilities'

import type {
  getEmployeeOrders,
  getEmployeeOrdersVariables,
} from 'Constants/graphql/queries/listing/__generated__/getEmployeeOrders'
import type {
  getEmployeeReturnCases,
  getEmployeeReturnCasesVariables,
} from 'Constants/graphql/queries/listing/__generated__/getEmployeeReturnCases'
import type {
  getOrders,
  getOrdersVariables,
} from 'Constants/graphql/queries/listing/__generated__/getOrders'
import { GET_EMPLOYEE_ORDERS } from 'Constants/graphql/queries/listing/GetEmployeeOrders'
import { GET_EMPLOYEE_RETURN_CASES } from 'Constants/graphql/queries/listing/GetEmployeeReturnCases'
import { GET_ORDERS } from 'Constants/graphql/queries/listing/GetOrders'
import { PortalSectionEnum } from 'Constants/portalSection'

import { ReportType } from 'Portal/__generated__/globalTypes'

export const OrdersDefault = observer(() => {
  const [selectedEntityId, setSelectedEntityId] = useState('')
  const [previewColumnType, setPreviewColumnType] =
    useState<OrdersPreviewColumnTypeEnum>(OrdersPreviewColumnTypeEnum.ORDER)

  const [previewedRowIndex, setPreviewedRowIndex] = useState<number | null>(null)
  const [isPreviewColumnOpen, setIsPreviewColumnOpen] = useState(false)
  const isSidebarOpen = isPreviewColumnOpen
  const { isVisible: isPreviewColumnVisible } = useSidebarVisibility(
    isPreviewColumnOpen,
    isSidebarOpen,
  )
  const permissions = usePermissions()
  const isOrganisation = useIsOrganisation()
  const intl = useIntl()
  const { employeeId } = useParams<{ employeeId?: string }>()

  const {
    data: orders,
    error,
    hasNextPage,
    intersectionObserver,
    isLoading,
    networkStatus,
    refetch,
    totalCount: ordersTotalCount,
  } = useDefaultListTable<
    getOrders,
    getOrdersVariables,
    getEmployeeOrders,
    getEmployeeOrdersVariables
  >({
    dataObjectPropertyName: 'caseItems',
    employeeQuery: GET_EMPLOYEE_ORDERS,
    organisationQuery: GET_ORDERS,
  })

  const { data: returnCasesData } = useQuery<
    getEmployeeReturnCases,
    getEmployeeReturnCasesVariables
  >(GET_EMPLOYEE_RETURN_CASES, {
    skip: isOrganisation || !employeeId,
    variables: { id: employeeId! },
  })

  const returnCases =
    returnCasesData?.employee.returnCases?.returnCases?.filter(value => !!value) ?? []
  const returnCasesTotalCount =
    returnCasesData?.employee.returnCases?.totalCount ?? 0

  const handleOpenPreviewColumn = useCallback(
    (
      id: string,
      rowIndex: number | null,
      previewColumnType: OrdersPreviewColumnTypeEnum,
    ) => {
      toggleIntercomVisibility(false)

      setSelectedEntityId(id)
      setPreviewedRowIndex(rowIndex)
      setPreviewColumnType(previewColumnType)
      setIsPreviewColumnOpen(true)
    },
    [],
  )

  const handleClosePreviewColumn = useCallback(() => {
    toggleIntercomVisibility(true)

    setIsPreviewColumnOpen(false)
    setPreviewedRowIndex(null)
  }, [])

  const handleSearch = useCallback(
    (value: string) => {
      refetch({ searchterm: value || null })
    },
    [refetch],
  )

  if (error) {
    return <PageErrorState />
  }

  if (isLoading && !orders) {
    return <PageLoadingState />
  }

  if (!orders) {
    return <PageErrorState />
  }

  const totalCaseItems = isOrganisation
    ? (ordersTotalCount ?? 0)
    : (ordersTotalCount ?? 0) + returnCasesTotalCount

  return (
    <Container className="pt-6" fluid>
      <div role="banner">
        {isOrganisation ? (
          <PageHeader portalSection={PortalSectionEnum.Orders} />
        ) : (
          <PageHeader portalSection={PortalSectionEnum.OrdersAndReturns} />
        )}
      </div>

      <Row>
        <Col xl={12} xxl={8}>
          <Card className="mb-4">
            <ListSearch
              downloadReportAlertAdditionalReadyMessage={intl.formatMessage({
                id: 'Download_report_report_ready_order_message',
              })}
              downloadReportType={
                hasPermission(permissions, permissions.modules.orders?.report)
                  ? ReportType.orders
                  : undefined
              }
              handleSearch={handleSearch}
              totalCount={totalCaseItems}
            />

            <Row>
              <Col>
                <OrdersTable
                  isActiveEmployee={!isOrganisation}
                  isLoading={isLoading}
                  isSearching={networkStatus === NetworkStatus.setVariables}
                  onOpenPreviewColumn={handleOpenPreviewColumn}
                  orders={orders}
                  previewedRowIndex={previewedRowIndex}
                  returns={returnCases}
                />
              </Col>
            </Row>
          </Card>

          <Row className="p-1">
            <Col>
              {hasNextPage && !isLoading && (
                <ObserveMeOnceIAmMounted
                  intersectionObserver={intersectionObserver}
                />
              )}
            </Col>
          </Row>
        </Col>

        <ContentSidebar
          isSidebarOpen={isPreviewColumnOpen}
          renderStickyColumnSideContent={
            <>
              <ImpactTracker />
              <FaqCard />
            </>
          }
        >
          {previewColumnType === OrdersPreviewColumnTypeEnum.RETURN ? (
            <ReturnsPreviewColumn
              entityId={selectedEntityId}
              isVisible={isPreviewColumnVisible}
              onClosePreviewColumn={handleClosePreviewColumn}
            />
          ) : (
            <OrdersPreviewColumn
              entityId={selectedEntityId}
              isVisible={isPreviewColumnVisible}
              onClosePreviewColumn={handleClosePreviewColumn}
            />
          )}
        </ContentSidebar>
      </Row>
    </Container>
  )
})
